import { Observable, of } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { AbstractService } from "../../../../services/abstract.service";
import { IAPIItemResult, IAPIListResult, IPagination, IPaginationResult, IPaginationResultFrequencies, IRequestResult } from "../../../../common/interfaces";
import { environment as env } from "../../../../../environments/environment.prod";
import { andWhere, buildQuery, CombinedFilter, FilterFn, noop, Normalizers, Ops, orderBy, OrderVariant, orWhere, page, select, where } from "@albatrosdeveloper/ave-utils-npm/lib/utils/query.util";
import { DocumentClassAttributes, MastersUserAttributes, OrderAttributes } from "@albatrosdeveloper/ave-models-package";
import { environment } from "environments/environment.qas";
import { OrderOperationTypeEnum, StatusEnum } from "@albatrosdeveloper/ave-models-package/lib/enums";
import { RequestOptions } from "app/common/interfaces/options.interface";
import { IFilters } from "../../filters/interfaces/filterInterface";
import { DocumentTypeEnum } from "app/common/enums/document-types.enum";
import { ApiResponseDataImport } from "app/shared/components/excel-import/components/import-dialog/import-dialog.component";
import { PreValidationResponse } from "app/shared/components/excel-import/interfaces/pre-validation.interface";
import { DateHelper } from "app/utils/date.helper";


const apiUrl = env.API_AVE_ORDER_URL;
const moduleName = 'order';
const documentUrl = env.API_DOCUMENT_URL + 'api/';
const masterUrl = env.API_UNIT_MEASURE + 'api/';
const integratorUrl = env.API_AVE_INTEGRATOR_URL + 'api/'
const pathOrderIntegrator = env.PATH_ORDER_INTEGRATOR;
const businessPartnerUrl = env.API_BUSINESS_PARTNER + 'api/';

/** Entregas de salida de UN viaje. `tripId` es `null` cuando la entrega no tiene viaje asignado. */
export interface DeliveryTripGroup {
  tripId: number | null;
  deliveries: OrderAttributes[];
}

/**
 * Resultado de {@link OrdersListService.findFutureOutboundDeliveriesByMaterial}.
 *
 * `available: false` significa que la consulta NO se pudo correr — el endpoint de órdenes
 * respondió 403 (requiere el permiso de listado de documentos de venta, que un administrador de
 * materiales puede no tener) o no había con qué consultar. Eso no es un error que reportar, es
 * una lista que no podemos mostrar: quien llama degrada a la advertencia solo de texto en vez de
 * afirmar "no hay entregas afectadas", que es lo que significaría `groups: []` con
 * `available: true`.
 */
export interface FutureDeliveriesByTrip {
  available: boolean;
  groups: DeliveryTripGroup[];
  /**
   * Cuántas entregas puede afirmar quien llama. Exacto con `truncated` en false; un PISO
   * ("más de estas") cuando es true.
   */
  total: number;
  /** Si el tope de página cortó el resultado, y entonces `total` subestima el impacto real. */
  truncated: boolean;
}

/**
 * Tope de página de la consulta de entregas afectadas. La advertencia es orientativa — le
 * muestra al usuario la forma del impacto, no una auditoría exhaustiva — así que una página
 * generosa es mejor que paginar dentro de un diálogo.
 */
const FUTURE_DELIVERIES_PAGE_SIZE = 200;

/**
 * Filas realmente pedidas: una más que el tope, solo para saber si el tope cortó.
 *
 * `GET api/order/filter` responde `IRequestResult<OrderAttributes[]>` — un arreglo pelado, sin
 * `pageInfo` y sin total. La fila extra no se renderiza nunca; solo permite decir "más de 200"
 * en lugar de "200", porque la advertencia nunca debe subestimar.
 */
const FUTURE_DELIVERIES_PROBE_SIZE = FUTURE_DELIVERIES_PAGE_SIZE + 1;

@Injectable({
  providedIn: "root",
})
export class OrdersListService extends AbstractService<
  OrderAttributes,
  Partial<OrderAttributes>,
  Partial<OrderAttributes>
> {
  currentFilter: CombinedFilter<OrderAttributes>;
  currentFilterData: any;

  constructor(
    http: HttpClient,
  ) {
    super(apiUrl, moduleName, http);
  }

  findByFilter(filter: CombinedFilter<OrderAttributes>, filterData: any, options?: RequestOptions) {
    const API_URL: string = apiUrl + `api/${moduleName}/withCountV2?filter=${encodeURIComponent(JSON.stringify(filter))}`;
    const headers: Record<string, any> = {};
    // see order-customer-export for more info about this header
    // ctx: stands for context
    if (options?.contextHeader) headers['x-ave-ctx'] = options.contextHeader;
    return this.http.post<IPaginationResultFrequencies<OrderAttributes>>(API_URL, filterData, { headers });
  }

  /**
   * Pagination
   * @param options Pagination options
   * @returns Paginated data
   */
  findAllWithCount(
    options: IPagination<OrderAttributes>,
    dataToFilter?,
  ): Observable<IPaginationResultFrequencies<OrderAttributes>> {
    const hasSearch = !!options.search;

    const buildBaseFilters = (params: { useOrWhere: boolean }): FilterFn<OrderAttributes>[] => {
      const filterFn = params.useOrWhere ? orWhere : andWhere;

      return [
        filterFn("_deleted", Ops.not(true)),
        andWhere("company.id" as keyof OrderAttributes, Ops.in(...[environment.companyId])),
        // 5 filtros de fechas
        dataToFilter.startDate && dataToFilter.endDate
          ? andWhere("creationDateTime", Ops.btwnDates(dataToFilter.startDate, dataToFilter.endDate))
          : noop(),

        dataToFilter.startTrip && dataToFilter.endTrip
          ? andWhere("trip.dateHourStartTeoric" as keyof OrderAttributes, Ops.btwnDates(dataToFilter.startTrip, dataToFilter.endTrip))
          : noop(),

        dataToFilter.end_startTrip && dataToFilter.end_endTrip
          ? andWhere("trip.dateHourEndTeoric" as keyof OrderAttributes, Ops.btwnDates(dataToFilter.end_startTrip, dataToFilter.end_endTrip))
          : noop(),

        dataToFilter.startDeliveryDate && dataToFilter.endDeliveryDate
          ? andWhere("deliveryDateTime", Ops.btwnDates(dataToFilter.startDeliveryDate, dataToFilter.endDeliveryDate))
          : noop(),

        dataToFilter.pickerUpload_start && dataToFilter.pickerUpload_end
          ? andWhere("loadDateTime", Ops.btwnDates(dataToFilter.pickerUpload_start, dataToFilter.pickerUpload_end))
          : noop(),

        // Filtro especial: ordersToFilter (solo cuando NO hay búsqueda)
        dataToFilter.ordersToFilter && dataToFilter.ordersToFilter.length > 0 && !hasSearch
          ? andWhere("serial", Ops.in(...dataToFilter.ordersToFilter))
          : noop(),

        // Filtros condicionales únicos
        dataToFilter.type
          ? andWhere(<any>"type.code", Ops.eq(dataToFilter.type))
          : noop(),

        dataToFilter.withTravel == '1'
          ? andWhere('trip', Ops.not(null))
          : dataToFilter.withTravel == '2'
            ? andWhere('trip', null)
            : noop(),

        // 11 filtros de arrays
        dataToFilter.documentClass && dataToFilter.documentClass.length > 0
          ? andWhere(<any>"class.code", Ops.in(...dataToFilter.documentClass))
          : noop(),

        dataToFilter.seller && dataToFilter.seller.length > 0
          ? andWhere(<any>"user.user_login.username", Ops.in(...dataToFilter.seller))
          : noop(),

        dataToFilter.statusERP && dataToFilter.statusERP.length > 0 && !dataToFilter.statusERP.includes(null)
          ? andWhere('statusERP', Ops.in(...dataToFilter.statusERP))
          : noop(),

        dataToFilter.status && dataToFilter.status.length > 0 && !dataToFilter.status.includes(null)
          ? andWhere('statusId', Ops.in(...dataToFilter.status))
          : noop(),

        dataToFilter.typeCustomer && dataToFilter.typeCustomer.length > 0 && !dataToFilter.typeCustomer.includes(null)
          ? andWhere('shipTo.types.code' as keyof OrderAttributes, Ops.in(...dataToFilter.typeCustomer))
          : noop(),

        dataToFilter.materialGroupId && dataToFilter.materialGroupId.length > 0 && !dataToFilter.materialGroupId.includes(null)
          ? andWhere('detail.material.materialGroups.code' as keyof OrderAttributes, Ops.in(...dataToFilter.materialGroupId))
          : noop(),

        dataToFilter.materialId && dataToFilter.materialId.length > 0 && !dataToFilter.materialId.includes(null)
          ? andWhere('detail.material.code' as keyof OrderAttributes, Ops.in(...dataToFilter.materialId))
          : noop(),

        dataToFilter.soldTo && dataToFilter.soldTo.length > 0
          ? andWhere(<any>'soldTo.id', Ops.in(...dataToFilter.soldTo))
          : noop(),

        dataToFilter.shipTo && dataToFilter.shipTo.length > 0
          ? andWhere(<any>'shipTo.id', Ops.in(...dataToFilter.shipTo))
          : noop(),

        dataToFilter.salesOfficeId && dataToFilter.salesOfficeId.length > 0
          ? andWhere('salesOffice.id' as keyof OrderAttributes, Ops.in(...dataToFilter.salesOfficeId))
          : noop(),

        dataToFilter.warehouseId && dataToFilter.warehouseId.length > 0
          ? andWhere('warehouses.id' as keyof OrderAttributes, Ops.in(...dataToFilter.warehouseId))
          : noop(),

        dataToFilter.subRouteId && dataToFilter.subRouteId.length > 0
          ? andWhere('subRoute.id' as keyof OrderAttributes, Ops.in(...dataToFilter.subRouteId))
          : noop(),

        dataToFilter.tripId
          ? andWhere('trip.id' as keyof OrderAttributes, Ops.eq(dataToFilter.tripId))
          : noop(),

        dataToFilter.operationType
          ? andWhere('operationType', Ops.eq(dataToFilter.operationType))
          : noop(),
      ];
    };

    // Objeto 1: Búsqueda por serial (número de pedido)
    const searchBySerialQueryObject: FilterFn<OrderAttributes>[] = [
      ...buildBaseFilters({ useOrWhere: false }),
      hasSearch
        ? andWhere("serial", Ops.like(options.search!, "i"))
        : noop(),
    ];

    // Objeto 2: Búsqueda por nombre del cliente (soldTo)
    const searchBySoldToNameQueryObject: FilterFn<OrderAttributes>[] = hasSearch
      ? [
          ...buildBaseFilters({ useOrWhere: true }),
          andWhere("soldTo.name" as keyof OrderAttributes, Ops.like(options.search!, "i")),
        ]
      : [];

    // Objeto 3: Búsqueda por nombre del destinatario (shipTo)
    const searchByShipToNameQueryObject: FilterFn<OrderAttributes>[] = hasSearch
      ? [
          ...buildBaseFilters({ useOrWhere: true }),
          andWhere("shipTo.name" as keyof OrderAttributes, Ops.like(options.search!, "i")),
        ]
      : [];

    // Objeto 4: Búsqueda por username del vendedor
    const searchByUsernameQueryObject: FilterFn<OrderAttributes>[] = hasSearch
      ? [
          ...buildBaseFilters({ useOrWhere: true }),
          andWhere("user.user_login.username" as keyof OrderAttributes, Ops.like(options.search!, "i")),
        ]
      : [];

    // Objeto 5: Búsqueda por nombre de ruta
    const searchBySubRouteNameQueryObject: FilterFn<OrderAttributes>[] = hasSearch
      ? [
          ...buildBaseFilters({ useOrWhere: true }),
          andWhere("subRoute.name" as keyof OrderAttributes, Ops.like(options.search!, "i")),
        ]
      : [];

    const query = buildQuery<OrderAttributes>(
      ...searchBySerialQueryObject,
      ...searchBySoldToNameQueryObject,
      ...searchByShipToNameQueryObject,
      ...searchByUsernameQueryObject,
      ...searchBySubRouteNameQueryObject,
      orderBy(
        options.sort ?? "createdAt",
        options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    this.currentFilter = query;

    const API_URL: string = apiUrl + `api/${moduleName}/withCountV3`;
    return this.http.post<IPaginationResultFrequencies<OrderAttributes>>(API_URL, {
      filter: query,
    });
  }

  findAllDownloadWithCount(
    options: IPagination<OrderAttributes>, dataToFilter?
  ): Observable<IPaginationResultFrequencies<OrderAttributes>> {

    let dataToSend = {
      search: options.search
    }

    const startDate = new Date(new Date(dataToFilter.creationDateTime).setHours(0, 0, 0, 0));
    const endDate = new Date(new Date(dataToFilter.creationDateTime).setHours(23, 59, 59, 999));

    const arrayQueries: FilterFn<OrderAttributes>[] = [];
    if (dataToFilter.type) {
      arrayQueries.push(andWhere("subRoute.id" as keyof OrderAttributes, Ops.eq(dataToFilter.route)))
    }

    const query = buildQuery<OrderAttributes>(
      where(<any>"company.id", Ops.in(...[environment.companyId])),
      andWhere(<any>"type.code", Ops.eq(dataToFilter.type)),
      andWhere("creationDateTime", Ops.btwnDates(startDate, endDate)),
      andWhere("salesOffice.id" as keyof OrderAttributes, Ops.eq(dataToFilter.office)),
      ...arrayQueries,
      andWhere("_deleted", Ops.not(true)),
      orderBy(
        options.sort ?? "creationDateTime",
        options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    const API_URL: string = apiUrl + `api/${moduleName}/withCountV2?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.post<IPaginationResultFrequencies<OrderAttributes>>(API_URL, dataToSend);
  }

  fetchDocumentClasses(filter?): Observable<IAPIListResult<DocumentClassAttributes>> {
    const query = buildQuery<DocumentClassAttributes>(
      where("_deleted", Ops.eq(false)),
      andWhere("statusId", StatusEnum.ACTIVE),
      filter && filter.type ?
      andWhere(<any>"documentType.code", Ops.eq(filter.type)) : noop(),
    )
    let API_URL: string = `${documentUrl}document_class/filter`;
    if (query) {
      API_URL += `?filter=${encodeURIComponent(JSON.stringify(query))}`;
    }
    return this.http.get<IAPIListResult<DocumentClassAttributes>>(API_URL);
  }

  fetchMasterUsers(): Observable<IAPIListResult<any>> {
    const query = buildQuery<MastersUserAttributes>(
      where("applications_ids", Ops.in(...[environment.applicationId])),
      andWhere("sub_applications_ids", Ops.in(...[environment.subApplicationId_csmobile]))
    )

    let API_URL: string = masterUrl + 'master_user/user';
    if (query) {
      API_URL += `?filter=${encodeURIComponent(JSON.stringify(query))}`;
    }
    return this.http.get<IAPIListResult<any>>(API_URL);
  }

  //Liberar pedidos
  releaseOrders(data: {orderSerials: string[]}){
    let API_URL: string = apiUrl + '/api/order/release_orders_to_send_erp';
    return this.http.post(API_URL, data);
  }

  // Para sincronizar pedidos y documentos de carga
  synchronizeOrders(dataToSend) {
    if (!pathOrderIntegrator) {
      console.warn('No se construyó API porque pathOrderIntegrator es null');
      return of(null);
    }

    let API_URL: string = integratorUrl + pathOrderIntegrator + (env.PATH_SEND_ERP ?? '');
    return this.http.post(API_URL, dataToSend);
  }

  // Para sincronizar facturas, mermas y devoluciones
  synchronizeOrdersOther(dataToSend) {
    if (!pathOrderIntegrator) {
      console.warn('No se construyó API porque pathOrderIntegrator es null');
      return of(null);
    }
    
    let API_URL: string = integratorUrl + pathOrderIntegrator + '/send_documents_to_erp';
    return this.http.post(API_URL, dataToSend);
  }

  getListInvoices(dataToSend){
    if (!pathOrderIntegrator) {
      console.warn('No se construyó API porque pathOrderIntegrator es null');
      return of(null);
    }

    let API_URL: string = integratorUrl + pathOrderIntegrator + '/get-fac';
    return this.http.post(API_URL, dataToSend);
  }

  synchronizeOrdersOtherV2(dataToSend){
    if (!pathOrderIntegrator) {
      console.warn('No se construyó API porque pathOrderIntegrator es null');
      return of(null);
    }

    let API_URL: string = integratorUrl + pathOrderIntegrator + '/sync-order-return';
    return this.http.post(API_URL, dataToSend);
  }

  // Para generar documento de transporte
  generateDocumentTransport(dataToSend) {
    const API_URL: string = apiUrl + `api/${moduleName}/generate_tdoc`
    return this.http.post(API_URL, dataToSend);
  }

  // Para anular documentos de transporte
  annularDocumentTransport(id: string, dataToSend): Observable<IRequestResult<OrderAttributes>> {
    const API_URL: string = apiUrl + `api/${moduleName}/tdoc/${id}`
    return this.http.patch<IRequestResult<OrderAttributes>>(API_URL, dataToSend);
  }

  updateV2(id: string, data): Observable<IRequestResult<OrderAttributes>> {
    let API_URL: string = apiUrl + `api/${moduleName}/v2/${id}`;
    return this.http.patch<IRequestResult<OrderAttributes>>(API_URL, data);
  }

  approveNextDocument(id, dataToSend) {
    let API_URL: string = apiUrl + `api/order/approve_generate_next/${id}`
    return this.http.post(API_URL, dataToSend);
  }

  getFileTemplateDeliver(): string {
    return env.DELIV_ROUTE_LOAD_TEMPLATE || null;
  }

  importFile(data: FormData) {
    const API_URL: string = apiUrl + `api/${moduleName}/uploadxls`;
    return this.http.post(API_URL, data);
  }

  fetchSoldTos(dataToSend): Observable<any>{
    let API_URL: string = businessPartnerUrl + `/business-partner/get_all_soldtos`
    return this.http.post<IAPIItemResult<any>>(API_URL, dataToSend);
  }

  fetchShipTos(dataToSend): Observable<any>{
    let API_URL: string = businessPartnerUrl + `/business-partner/get_all_shiptos`
    return this.http.post<IAPIItemResult<any>>(API_URL, dataToSend);
  }

    updateNoteInvoice(id: string, data): Observable<IRequestResult<OrderAttributes>> {
    let API_URL: string = apiUrl + `api/${moduleName}/update_note/${id}`;
    return this.http.patch<IRequestResult<OrderAttributes>>(API_URL, data);
  }

    findToLoadDocument(
      serial: string
    ): Observable<IRequestResult<OrderAttributes[]>> {
      const query = buildQuery<OrderAttributes>(
        where("_deleted", Ops.eq(false)),
         serial
          ? andWhere(
            "sourceTransactions.serial" as keyof OrderAttributes,
            Ops.eq(serial)
          )
          : noop(),
        andWhere("type.code" as keyof OrderAttributes, Ops.eq(DocumentTypeEnum.DOCUMENTO_DE_CARGA)),
        select(["serial"]),
      );

      const API_URL: string =
        apiUrl +
        `api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
      return this.http.get<IRequestResult<OrderAttributes[]>>(API_URL);
    }

    // TODO: Implement this method
  getOrderTemplateUrl(): string {
    return env.ORDERS_IMPORT_TEMPLATE_URL;
  }

  importOrdersFromExcel(formData: FormData): Observable<ApiResponseDataImport> {
    const API_URL: string = apiUrl + `api/order_import/upload/orders`;
    return this.http.post<ApiResponseDataImport>(API_URL, formData);
  }

  /**
   * Pre-valida un archivo Excel de pedidos antes de la importación final
   * @param formData FormData con el archivo Excel a validar
   * @returns Observable con el resultado de la pre-validación
   */
  preValidateOrdersFromExcel(formData: FormData): Observable<PreValidationResponse> {
    const API_URL: string = apiUrl + `api/order_import/pre-validate`;
    return this.http.post<PreValidationResponse>(API_URL, formData);
  }

  findToLoadDocumentUnified(
      serial: string
    ): Observable<IRequestResult<OrderAttributes[]>> {
      const query = buildQuery<OrderAttributes>(
        where("_deleted", Ops.eq(false)),
        andWhere("operationType", Ops.eq(OrderOperationTypeEnum.ORDER_JOIN)),
         serial
          ? andWhere(
            "sourceTransactions.serial" as keyof OrderAttributes,
            Ops.eq(serial)
          )
          : noop(),
        andWhere("type.code" as keyof OrderAttributes, Ops.eq(DocumentTypeEnum.DOCUMENTO_DE_CARGA)),
        select(["serial"]),
      );

      const API_URL: string =
        apiUrl +
        `api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
      return this.http.get<IRequestResult<OrderAttributes[]>>(API_URL);
    }

  /**
   * Entregas de salida (DT011) futuras que llevan un material, agrupadas por viaje.
   *
   * Alimenta la advertencia que se muestra ANTES de desactivar la selección manual de etiquetas
   * o de cambiar la unidad de control logístico de un material: esas entregas son las que
   * cambiarían de comportamiento en la APP de Field Sales. Vive acá y no en un servicio de
   * material porque todo lo que necesita es vocabulario de ÓRDENES que este servicio ya tiene:
   * el endpoint, el alcance por compañía (`company._id`) y el enum de tipos de documento.
   *
   * Usa `GET api/order/filter` (igual que {@link findToLoadDocument}) y no `withCountV3`, porque
   * esta consulta NECESITA la proyección y `withCountV3` no la acepta: cualquier `select()` le
   * saca un 400, y una página de 200 filas sin proyección pesa megabytes. Un diálogo de
   * advertencia no puede costar eso.
   *
   * @param materialCode  el `code` del material; se compara contra `detail.material.code`, el
   *                      mismo campo por el que el listado de órdenes filtra materiales (la
   *                      entrega embebe la foto del material, no una referencia).
   * @param companyMongoId  la compañía del material. En esta rama un material pertenece a UNA
   *                      compañía, así que el filtro es un `eq` y no un `in`.
   */
  findFutureOutboundDeliveriesByMaterial(
    materialCode: string,
    companyMongoId: string
  ): Observable<FutureDeliveriesByTrip> {
    // Nada con qué consultar — sin `code` guardado o sin compañía. `available: false`, NO un
    // resultado vacío disponible: la consulta nunca corrió, así que el diálogo no debe
    // renderizar "no hay entregas afectadas", que afirmaría como hecho algo que nadie verificó.
    if (!materialCode || !companyMongoId) {
      return of<FutureDeliveriesByTrip>({
        available: false,
        groups: [],
        total: 0,
        truncated: false,
      });
    }

    const query = buildQuery<OrderAttributes>(
      where("type.code" as keyof OrderAttributes, Ops.eq(DocumentTypeEnum.ENTREGA_SALIDA)),
      andWhere("detail.material.code" as keyof OrderAttributes, Ops.in(materialCode)),
      andWhere(
        "company._id" as keyof OrderAttributes,
        Ops.eq(companyMongoId, Normalizers.ObjectId)
      ),
      // `not(true)` y no `eq(false)`: una entrega creada antes de que el campo existiera no
      // tiene `isMaterialized`, y un flag AUSENTE significa "todavía no materializada" — la
      // misma lectura que `_deleted` recibe en todo este servicio.
      andWhere("isMaterialized" as keyof OrderAttributes, Ops.not(true)),
      andWhere("statusId", Ops.eq(StatusEnum.ACTIVE)),
      // Desde el INICIO de hoy: una entrega programada para más temprano hoy sigue por delante
      // del repartidor. `getStartOfDay` ya retrocede 1 ms para incluir la fila de 00:00:00.
      andWhere("deliveryDateTime", Ops.gte(DateHelper.getStartOfDay(new Date()))),
      andWhere("_deleted", Ops.not(true)),
      orderBy("deliveryDateTime", OrderVariant.Asc),
      select([
        "_id",
        "serial",
        "deliveryDateTime",
        "trip._id",
        "trip.id",
      ] as unknown as Array<keyof OrderAttributes>),
      page(1, FUTURE_DELIVERIES_PROBE_SIZE)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IRequestResult<OrderAttributes[]>>(API_URL).pipe(
      map((response) => {
        const rows = response?.data ?? [];
        // La fila sonda nunca se renderiza; solo informa si el tope cortó.
        return groupDeliveriesByTrip(
          rows.slice(0, FUTURE_DELIVERIES_PAGE_SIZE),
          rows.length > FUTURE_DELIVERIES_PAGE_SIZE
        );
      }),
      // Un administrador de materiales puede no tener el permiso de listar órdenes, y el
      // endpoint responde 403. Eso es esperable acá, no una falla que merezca un toast: quien
      // llama muestra la advertencia sin la lista.
      catchError(() =>
        of<FutureDeliveriesByTrip>({
          available: false,
          groups: [],
          total: 0,
          truncated: false,
        })
      )
    );
  }
}

/**
 * Agrupa entregas por el `id` numérico plano de su viaje, conservando el orden en que llegaron
 * dentro de cada grupo y el orden en que los viajes aparecen por primera vez. Las entregas sin
 * viaje asignado caen en un único grupo `tripId: null`, listado al final: son impacto real, solo
 * que todavía no programado.
 *
 * @param truncated si quien llama ya descartó filas pasadas del tope, y entonces `total` es un
 *                  piso y no la cuenta.
 */
export function groupDeliveriesByTrip(
  deliveries: OrderAttributes[],
  truncated = false
): FutureDeliveriesByTrip {
  const byTrip = new Map<number | null, OrderAttributes[]>();
  for (const delivery of deliveries) {
    const tripId = delivery?.trip?.id != null ? Number(delivery.trip.id) : null;
    const group = byTrip.get(tripId);
    if (group) {
      group.push(delivery);
    } else {
      byTrip.set(tripId, [delivery]);
    }
  }
  const groups: DeliveryTripGroup[] = [...byTrip.entries()]
    .map(([tripId, list]) => ({ tripId, deliveries: list }))
    .sort((a, b) => (a.tripId === null ? 1 : b.tripId === null ? -1 : 0));

  return { available: true, groups, total: deliveries.length, truncated };
}
